/**
 * Client approval page (US-18). Public: /approve/:token on the partners host, no login. The token is the only credential;
 * the server decides what is shown and answers the same 404 for an unknown, expired, revoked or out-of-scope link.
 * English and Arabic (RTL), phone first. Strings go through useSurface().t. Owner: partners (content tracker).
 */
import './content.css';
import './approval.css';
import { useMemo, useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import { Button, Heading, Text } from '@qm/ui';
import type { Locale, Translate } from '@qm/i18n';
import { CATALOG } from './layout';
import { fmtDate } from './content';
import { contentApi, type PublicApproval, type PublicApprovalItem } from '../api/content';
import { SurfaceProvider, useSurface } from '../kit/context';
import { errorInfo } from '../kit/http';
import { useAction, useResource } from '../kit/resource';
import { Badge, Field, Notice, errorMessage, type Tone } from '../kit/ui';

const LANGUAGE_NAMES: Record<Locale, string> = { en: 'English', ar: 'العربية' };
type Choice = 'approved' | 'changes_requested';

/** Maps a failed call to what the client can act on. A 404 never says whether the item exists. */
export function answerError(t: Translate, err: unknown): string {
  const info = errorInfo(err);
  if (info.reason === 'version_conflict') return t('portal.partners.approve.conflict');
  if (info.status === 404) return t('portal.partners.approve.invalid');
  if (info.status === 429) return t('portal.partners.approve.rateLimited');
  return errorMessage(t, err);
}

export function ApprovalPage() {
  const { token = '' } = useParams();
  // starts in the surface's language (the browser's, or the one the client already chose on this device)
  const [locale, setLocale] = useState<Locale>(useSurface().locale);
  return (
    <SurfaceProvider catalog={CATALOG} locale={locale}>
      <ApprovalBody token={token} onLocale={setLocale} />
    </SurfaceProvider>
  );
}

function ApprovalBody({ token, onLocale }: { token: string; onLocale: (l: Locale) => void }) {
  const { t, dir, locale, http } = useSurface();
  const api = useMemo(() => contentApi(http), [http]);
  const approval = useResource<PublicApproval>(`approval:${token}`, () => api.publicApproval(token));
  return (
    <main className="qa-page" dir={dir} lang={locale} data-testid="approval-page">
      <div className="qa-lang" role="group" aria-label={t('portal.partners.approve.language')}>
        {(Object.keys(LANGUAGE_NAMES) as Locale[]).map((l) => (
          <button key={l} type="button" lang={l} aria-pressed={locale === l} onClick={() => onLocale(l)}>{LANGUAGE_NAMES[l]}</button>
        ))}
      </div>
      {approval.status === 'loading' ? <Text tone="muted">{t('portal.kit.loading')}</Text> : null}
      {approval.status === 'error' ? (
        <Notice tone="warn" testId="approval-unavailable">{answerError(t, approval.error)}</Notice>
      ) : null}
      {approval.status === 'ready' ? <ApprovalContent data={approval.data} token={token} onChanged={approval.reload} /> : null}
    </main>
  );
}

function ApprovalContent({ data, token, onChanged }: { data: PublicApproval; token: string; onChanged: () => void }) {
  const { t, locale } = useSurface();
  return (
    <>
      <header className="qa-head">
        <Heading level={1}>{t('portal.partners.approve.title')}</Heading>
        <Text>{t('portal.partners.approve.intro')}</Text>
        <Text tone="muted">
          {t('portal.partners.approve.client', { client: data.clientName })}
          {' · '}
          {t('portal.partners.approve.agency', { agency: data.agencyName })}
        </Text>
        <Text tone="muted">{t('portal.partners.approve.expires', { date: fmtDate(locale, data.expiresAt.slice(0, 10)) })}</Text>
      </header>
      {data.items.length === 0 ? <Notice testId="approval-empty">{t('portal.partners.approve.empty')}</Notice> : null}
      {data.items.map((item) => (
        <ItemCard key={item.id} item={item} token={token} agency={data.agencyName} onDone={onChanged} />
      ))}
    </>
  );
}

function ItemCard({ item, token, agency, onDone }: { item: PublicApprovalItem; token: string; agency: string; onDone: () => void }) {
  const { t, locale, http } = useSurface();
  const api = useMemo(() => contentApi(http), [http]);
  const [choice, setChoice] = useState<Choice | null>(item.approval === 'approved' || item.approval === 'changes_requested' ? item.approval : null);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [nameMissing, setNameMissing] = useState(false);
  const [saved, setSaved] = useState<Choice | null>(null);
  const answer = useAction((body: Parameters<typeof api.answerApproval>[1]) => api.answerApproval(token, body));
  const latest = item.files[0];
  const earlier = item.files.slice(1);
  const status = item.approval === 'approved' ? 'approved' : item.approval === 'changes_requested' ? 'changes_requested' : 'pending';
  const tone: Tone = status === 'approved' ? 'good' : status === 'changes_requested' ? 'warn' : 'neutral';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!choice) return;
    if (!name.trim()) { setNameMissing(true); return; }
    setNameMissing(false);
    const done = await answer.run({ deliverableId: item.id, approval: choice, clientName: name.trim(), ...(comment.trim() ? { comment: comment.trim() } : {}) });
    if (done) {
      setSaved(choice);
      setComment('');
      onDone();
    }
  };

  return (
    <section className="qc-panel qa-item" aria-labelledby={`approve-${item.id}`} data-testid="approval-item">
      <div className="qa-item__head">
        <Heading level={2} id={`approve-${item.id}`}>{item.title}</Heading>
        <Badge tone={tone} testId="approval-item-status">{t(`portal.partners.approve.status.${status}`)}</Badge>
      </div>
      {item.deliveredOn ? <Text tone="muted">{t('portal.partners.approve.delivered', { date: fmtDate(locale, item.deliveredOn) })}</Text> : null}

      {latest ? (
        <div className="qa-files">
          <a className="qa-file" href={latest.url} target="_blank" rel="noopener noreferrer">
            {t('portal.partners.approve.open')}: {latest.label || latest.url}
          </a>
          <small className="qc-hint">
            {t('portal.partners.content.item.version', { n: latest.versionNo })} · {t('portal.partners.approve.latest')}
          </small>
          {earlier.length > 0 ? (
            <details>
              <summary>{t('portal.partners.approve.earlier')}</summary>
              <ul className="qc-list">
                {earlier.map((f) => (
                  <li key={f.versionNo}>
                    <a href={f.url} target="_blank" rel="noopener noreferrer">{t('portal.partners.content.item.version', { n: f.versionNo })}{f.label ? ` · ${f.label}` : ''}</a>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      ) : <Text tone="muted">{t('portal.partners.approve.noFiles')}</Text>}

      <form onSubmit={(e) => void submit(e)} className="qa-form" aria-label={t('portal.partners.approve.formLabel', { title: item.title })}>
        <fieldset className="qa-choices">
          <legend>{t('portal.partners.approve.decision')}</legend>
          <label>
            <input type="radio" name={`decision-${item.id}`} value="approved" checked={choice === 'approved'} onChange={() => setChoice('approved')} />
            {t('portal.partners.approve.optionApprove')}
          </label>
          <label>
            <input type="radio" name={`decision-${item.id}`} value="changes_requested" checked={choice === 'changes_requested'} onChange={() => setChoice('changes_requested')} />
            {t('portal.partners.approve.optionChanges')}
          </label>
        </fieldset>
        <Field label={t('portal.partners.approve.name')} hint={t('portal.partners.approve.nameHint')} error={nameMissing ? t('portal.partners.approve.nameRequired') : null}>
          {(id, desc) => (
            <input id={id} name="clientName" type="text" autoComplete="name" maxLength={120} required value={name} aria-describedby={desc}
              onChange={(e) => setName(e.target.value)} />
          )}
        </Field>
        <Field label={t('portal.partners.approve.comment')} hint={t('portal.partners.approve.commentHint')}>
          {(id, desc) => (
            <textarea id={id} name="comment" rows={3} maxLength={1000} value={comment} aria-describedby={desc} onChange={(e) => setComment(e.target.value)} />
          )}
        </Field>
        <div>
          <Button type="submit" disabled={!choice || answer.pending}>{t('portal.partners.approve.submit')}</Button>
        </div>
      </form>
      {answer.error ? <div className="qx-alert" role="alert" data-testid="approval-error">{answerError(t, answer.error)}</div> : null}
      {saved ? <Notice tone="good" testId="approval-saved">{t('portal.partners.approve.saved', { agency })}</Notice> : null}
    </section>
  );
}
